import React, { useState, useEffect, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import driverAPI from '../api/driverAPI';

const DriverSearch = ({
  value = null,       // { _id, name, nickname, ... } or null
  onChange,           // called with driver object on select, null on clear
  placeholder = 'Search driver by name or nickname…',
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm]     = useState('');
  const [results, setResults]           = useState([]);
  const [loading, setLoading]           = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected]         = useState(value);
  const containerRef = useRef(null);

  // Sync external value prop
  useEffect(() => {
    if (value && typeof value === 'object') {
      setSelected(value);
      setSearchTerm(value.nickname || value.name || '');
    } else if (!value) {
      setSelected(null);
      setSearchTerm('');
    }
  }, [value]);

  const doSearch = useCallback(
    debounce(async (term) => {
      if (!term || term.trim().length < 1) {
        setResults([]);
        return;
      }
      setLoading(true);
      try {
        const res = await driverAPI.search(term);
        setResults(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setShowDropdown(true);
    if (selected) {
      setSelected(null);
      onChange?.(null);
    }
    doSearch(term);
  };

  const handleSelect = (driver) => {
    setSelected(driver);
    setSearchTerm(driver.nickname || driver.name || '');
    setShowDropdown(false);
    setResults([]);
    onChange?.(driver);
  };

  const handleClear = () => {
    setSelected(null);
    setSearchTerm('');
    setResults([]);
    setShowDropdown(false);
    onChange?.(null);
  };

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const showNoResults =
    showDropdown && searchTerm.trim().length >= 1 && results.length === 0 && !loading;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
        />
        {selected && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {loading && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <svg className="animate-spin h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-56 overflow-y-auto">
          {results.map((d) => (
            <div
              key={d._id}
              onClick={() => handleSelect(d)}
              onMouseDown={(e) => e.preventDefault()}
              className="px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-teal-50 cursor-pointer"
            >
              <div className="font-medium text-gray-900 text-sm">
                {d.nickname ? `${d.nickname} — ${d.name}` : d.name}
              </div>
              {(d.truckColor || d.truckPlateCode) && (
                <div className="text-xs text-gray-500 mt-0.5">
                  {[d.truckColor, d.truckPlateCode].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showNoResults && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-sm text-gray-500 text-center">
          No drivers found for "{searchTerm}"
        </div>
      )}
    </div>
  );
};

export default DriverSearch;
